import { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { X } from "lucide-react"
import { getPayment, updatePayment } from "../../services/paymentService"
import { fr } from "../../locales/fr"
import { Button } from "../ui/Button"
import { Label } from "../ui/Label"
import { Select } from "../ui/Select"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/Card"

const textareaClass = `
  flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground
  placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring
  focus-visible:ring-offset-2 focus-visible:ring-offset-background
`

export function PaymentInvoiceModal({ open, onClose, paymentId, onSaved }) {
  const inv = fr.invoice
  const [paymentType, setPaymentType] = useState("full")
  const [invoiceNotes, setInvoiceNotes] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    if (!open || !paymentId) {
      setLoaded(false)
      return
    }
    setError("")
    setLoaded(false)
    setLoading(true)
    getPayment(paymentId)
      .then((p) => {
        setPaymentType(p.paymentType === "partial" ? "partial" : "full")
        setInvoiceNotes(p.invoiceNotes || "")
        setLoaded(true)
      })
      .catch(() => setError(inv.loadError))
      .finally(() => setLoading(false))
  }, [open, paymentId])

  useEffect(() => {
    if (!open) return
    const onKey = (e) => {
      if (e.key === "Escape") onClose()
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [open, onClose])

  const save = async () => {
    if (!paymentId) return
    setError("")
    setLoading(true)
    try {
      await updatePayment(paymentId, {
        payment_type: paymentType,
        invoice_notes: invoiceNotes.trim() || null,
      })
      onSaved?.()
      onClose()
    } catch {
      setError(inv.saveError)
    } finally {
      setLoading(false)
    }
  }

  if (!open) return null

  const modal = (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-background/80 backdrop-blur-sm"
        aria-label={fr.common.close}
        onClick={onClose}
      />
      <Card className="relative z-10 w-full max-w-md border-border shadow-lg">
        <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
          <div className="space-y-1.5">
            <CardTitle className="text-lg">{inv.editTitle}</CardTitle>
            <CardDescription>{inv.editHint}</CardDescription>
          </div>
          <Button type="button" variant="ghost" size="icon" className="shrink-0" onClick={onClose} aria-label={fr.common.cancel}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading && !loaded ? <p className="text-sm text-muted-foreground">{fr.common.loading}</p> : null}
          <div className="space-y-2">
            <Label htmlFor="inv-payment-type">{inv.paymentTypeLabel}</Label>
            <Select
              id="inv-payment-type"
              value={paymentType}
              onChange={(e) => setPaymentType(e.target.value)}
            >
              <option value="full">{inv.paymentTypeFull}</option>
              <option value="partial">{inv.paymentTypePartial}</option>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="inv-notes">{inv.invoiceNotes}</Label>
            <textarea
              id="inv-notes"
              value={invoiceNotes}
              onChange={(e) => setInvoiceNotes(e.target.value)}
              rows={5}
              className={textareaClass}
              placeholder={inv.notesPlaceholder}
            />
          </div>
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
          <div className="flex flex-wrap justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              {fr.common.cancel}
            </Button>
            <Button type="button" onClick={save} disabled={loading}>
              {loading ? fr.common.saving : inv.save}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )

  return createPortal(modal, document.body)
}
